import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Button, IconButton, Tooltip, useTheme, CircularProgress } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import jsPDF from "jspdf";
import "jspdf-autotable";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../../../components/Header";
import { tokens } from "../../../theme";

import { API_BASE, authFetch } from "../../../Utils/apiClient";

const SubscribedUsers = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSubscribers = async () => {
    setLoading(true);
    try {
      const response = await authFetch("/package/getAllSubscribers");
      const json = await response.json();
      const rows = (json.result || []).map((item) => ({
        id: item.id,
        userId: item.userId,
        registrarId: item.subscriptionId || item.packageId,
        name: item.User?.profileName || "N/A",
        zipCode: item.Package?.typeOfSubscription || "N/A",
        age: item.Package?.amount ?? 0,
        endDate: item.endDate
          ? new Date(item.endDate).toLocaleDateString("en-US")
          : "N/A",
        status: item.active ? "Active" : "Inactive",
      }));
      setData(rows);
    } catch (error) {
      console.error("Failed to load subscribers:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const columns = [
    { field: "userId", headerName: "User ID", width: 90 },
    { field: "registrarId", headerName: "Subscription ID", flex: 1, minWidth: 160 },
    { field: "name", headerName: "User Name", flex: 1, minWidth: 140 },
    { field: "zipCode", headerName: "Type of Subscription", flex: 1, minWidth: 150 },
    { field: "age", headerName: "Charges ($)", width: 110 },
    { field: "endDate", headerName: "End Date", width: 120 },
    { field: "status", headerName: "Status", width: 100 },
    {
      field: "Actions",
      headerName: "Actions",
      width: 100,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View">
            <Link to={`/subscribedusers/viewsubscribeduser/${params.row.id}`}>
              <IconButton>
                <VisibilityIcon />
              </IconButton>
            </Link>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton onClick={() => handleDeleteClick(params.row.id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const handleDeleteClick = async (id) => {
    if (!window.confirm("Remove this subscription record?")) return;
    try {
      const response = await authFetch(`/package/deleteSubscribe/${id}`, {
        method: "DELETE",
      });
      const json = await response.json();
      if (json.status) {
        setData((prev) => prev.filter((item) => item.id !== id));
      } else {
        alert(json.message || "Delete failed");
      }
    } catch (error) {
      console.error("Delete subscriber failed:", error);
      alert("Delete failed");
    }
  };

  const exportToPdf = () => {
    const doc = new jsPDF();
    doc.autoTable({
      head: [
        [
          "User ID",
          "Subscription ID",
          "User Name",
          "Type of Subscription",
          "Charges",
          "End Date",
          "Status",
        ],
      ],
      body: data.map(
        ({ userId, registrarId, name, zipCode, age, endDate, status }) => [
          userId,
          registrarId,
          name,
          zipCode,
          age,
          endDate,
          status,
        ]
      ),
    });
    doc.save("subscribed_users_data.pdf");
  };

  if (loading) {
    return (
      <Box m="20px" display="flex" justifyContent="center" alignItems="center" height="40vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="-10px">
        <Header title="Subscribed Users" subtitle={`${data.length} active subscriptions`} />
      </Box>

      <Box
        m="10px 0 0 0"
        height="55vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none", fontSize: "14px" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.greenAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.greenAccent[700],
          },
        }}
      >
        <Button
          variant="contained"
          onClick={exportToPdf}
          sx={{ backgroundColor: "#4caf50", color: "white", fontSize: "10px", mb: 1 }}
        >
          Export to PDF
        </Button>
        <DataGrid rows={data} columns={columns} components={{ Toolbar: GridToolbar }} />
      </Box>
    </Box>
  );
};

export default SubscribedUsers;
